import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Building2, LogOut, Package, ShoppingCart, User, Mail, Phone, Send,
  Plus, Minus, X, ImageIcon, Search, Clock, CheckCircle2, Truck,
  ChevronLeft, ChevronRight, Users, Home, UserCheck, CreditCard,
  Eye, EyeOff, Calendar, FileText, Trash2, MoreHorizontal,
} from "lucide-react";

const TABS = [
  { key: "catalogue",  label: "Catalogue",     icon: Package },
  { key: "commandes",  label: "Mes commandes", icon: ShoppingCart },
  { key: "clients",    label: "Mes clients",   icon: Users },
  { key: "profil",     label: "Profil",        icon: User },
  { key: "contact",    label: "Contact",       icon: Mail },
];

// BottomNav mobile (Catalogue / Commandes / Clients / Plus)
const BOTTOM_NAV = [
  { key: "catalogue", label: "Catalogue", icon: Package },
  { key: "commandes", label: "Commandes", icon: ShoppingCart },
  { key: "clients",   label: "Clients",   icon: Users },
  { key: "__plus",    label: "Plus",      icon: MoreHorizontal },
];

// Étiquette de stock côté B2B — sans jamais montrer le stock exact.
// Règle : si en_precommande est actif, on affiche UNIQUEMENT "Précommande" (jamais un statut stock contradictoire).
function stockBadge(p) {
  if (p?.en_precommande) return { label: "Précommande",     cls: "bg-orange-100 text-orange-700" };
  const s = Number(p?.stock_restant ?? 0);
  if (s > 100) return { label: "100+ disponibles", cls: "bg-emerald-100 text-emerald-700" };
  if (s > 5)   return { label: "En stock",         cls: "bg-emerald-100 text-emerald-700" };
  if (s > 0)   return { label: "Stock limité",     cls: "bg-amber-100 text-amber-800" };
  return       { label: "Rupture",                 cls: "bg-red-100 text-red-700" };
}

// Date d'arrivée précommande — ne jamais afficher une date passée
function preorderDateLabel(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return null;
    return d.toLocaleDateString("fr-BE", { day: "numeric", month: "long" });
  } catch { return null; }
}

const STATUT_LABELS = {
  en_attente_validation: { label: "En attente validation", color: "bg-amber-100 text-amber-700", Icon: Clock },
  en_attente: { label: "En préparation", color: "bg-blue-100 text-blue-700", Icon: Clock },
  en_cours: { label: "En route", color: "bg-indigo-100 text-indigo-700", Icon: Truck },
  livree: { label: "Livrée", color: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  annulee: { label: "Annulée", color: "bg-red-100 text-red-700", Icon: X },
  reportee: { label: "Reportée", color: "bg-slate-100 text-slate-700", Icon: Clock },
};

const B2BEspace = () => {
  const nav = useNavigate();
  const token = localStorage.getItem("b2b_token");
  const ent = JSON.parse(localStorage.getItem("b2b_ent") || "null");
  const user = JSON.parse(localStorage.getItem("b2b_user") || "null");
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState("catalogue");
  const [produits, setProduits] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [cart, setCart] = useState([]);
  const [productQuery, setProductQuery] = useState("");
  const [contactForm, setContactForm] = useState({ nom: user?.nom || "", email: user?.email || "", telephone: "", sujet: "", message: "" });
  const [profil, setProfil] = useState({ nom: user?.nom || "", telephone: "" });
  const [orderForm, setOrderForm] = useState({ contact_nom: user?.nom || "", contact_telephone: "", contact_email: user?.email || "", adresse_livraison: "", ville: "", code_postal: "", date_souhaitee: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailImgIdx, setDetailImgIdx] = useState(0);
  const [contactInfo, setContactInfo] = useState({ email: "", telephone: "", adresse: "Belgique", company_name: "TopCocoon" });
  // Nouveau flow livraison
  const [livrerChez, setLivrerChez] = useState("b2b");                // "b2b" | "client_final"
  const [livraisonPayePar, setLivraisonPayePar] = useState("b2b");    // "b2b" | "client_final"
  const [modePaiement, setModePaiement] = useState("virement");       // "virement" | "especes" | "stripe"
  const [clientFinal, setClientFinal] = useState({ nom: "", prenom: "", telephone: "", email: "", adresse: "", ville: "", code_postal: "" });
  const [clientQuery, setClientQuery] = useState("");
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [mesClients, setMesClients] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const [discret, setDiscret] = useState(() => localStorage.getItem("b2b_discret") === "1");
  const [fraisPreview, setFraisPreview] = useState(null); // {frais, zone}
  // Phase 3 — flow panier → vérification → confirmation
  const [checkoutStep, setCheckoutStep] = useState("cart"); // "cart" | "verify" | "success"
  // Sur la page Catalogue : panier compact par défaut ; formulaire livraison/paiement révélé après clic "Passer la commande"
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null); // { numero_commande, id, cart_snapshot, client_final_snapshot, livraison_frais, livrer_chez, ... }
  const [recuModal, setRecuModal] = useState(null);       // {commande, lignes: [{modele, couleur, quantite, prix_unitaire}]}

  const toggleDiscret = () => {
    const v = !discret;
    setDiscret(v);
    localStorage.setItem("b2b_discret", v ? "1" : "0");
  };
  // Prix masqués en mode présentation client — utilisé via `{!discret && …}` dans le rendu

  const openRecuModal = async (c) => {
    let defaults = new Map();
    // Charge les prix retail par défaut de la société (si configurés)
    try {
      const r = await axios.get(`${API}/b2b/me/entreprise`, { headers });
      const defs = r.data?.prix_retail_defaut || [];
      defaults = new Map(defs.map(d => [d.sku, d.prix_ttc_retail]));
    } catch { /* silent */ }
    const existing = c.prix_client_final_lignes || [];
    const map = new Map(existing.map(l => [`${l.modele}|${l.couleur}`, l.prix_unitaire]));
    const lignes = (c.produits_lies || []).map(p => {
      const savedPrice = map.get(`${p.modele}|${p.couleur}`);
      const defaultPrice = defaults.get(p.sku);
      return {
        modele: p.modele || "",
        couleur: p.couleur || "",
        quantite: p.quantite || 0,
        prix_unitaire: savedPrice || defaultPrice || 0,
        sku: p.sku,
      };
    });
    setRecuModal({ commande: c, lignes });
  };

  const updateLignePrix = (idx, val) => {
    setRecuModal(prev => {
      const lignes = [...prev.lignes];
      lignes[idx] = { ...lignes[idx], prix_unitaire: parseFloat(val) || 0 };
      return { ...prev, lignes };
    });
  };
  const totalRecuModal = recuModal ? recuModal.lignes.reduce((a, l) => a + l.prix_unitaire * l.quantite, 0) : 0;

  const saveAndOpenRecu = async () => {
    if (!recuModal) return;
    try {
      await axios.put(`${API}/b2b/livraisons/${recuModal.commande.id}/prix-client`, {
        prix_client_final_ttc: totalRecuModal,
        lignes: recuModal.lignes,
      }, { headers });
      const r = await axios.get(`${API}/b2b/livraisons/${recuModal.commande.id}/recu`, { headers, responseType: "text" });
      const w = window.open("", "_blank");
      w.document.write(r.data);
      w.document.close();
      setRecuModal(null);
      // Refresh commandes list
      const c = await axios.get(`${API}/b2b/me/commandes`, { headers });
      setCommandes(c.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const openRecu = openRecuModal;  // alias — ancien nom conservé pour rétrocompatibilité

  // Preview frais de livraison (débounce via useEffect sur CP)
  useEffect(() => {
    if (livrerChez !== "client_final") { setFraisPreview(null); return; }
    const cp = clientFinal.code_postal;
    if (!cp || cp.length < 4) { setFraisPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/frais-livraison-preview?cp=${encodeURIComponent(cp)}&pays=Belgique`);
        setFraisPreview(r.data);
      } catch { setFraisPreview(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [clientFinal.code_postal, livrerChez]);

  // Retour Stripe (success/cancel) via URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const p = params.get("payment");
    if (!p) return;
    if (p === "success") {
      toast.success("Paiement effectué ✅");
      setTab("commandes");
    } else if (p === "cancel") {
      toast.error("Paiement annulé — vous pouvez réessayer");
      setTab("commandes");
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const payerCommande = async (livId) => {
    setPayingId(livId);
    try {
      const r = await axios.post(`${API}/b2b/livraisons/${livId}/checkout`, { origin_url: window.location.origin, scope: "total" }, { headers });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur Stripe");
      setPayingId(null);
    }
  };

  useEffect(() => {
    if (!token) { nav("/b2b-login"); return; }
    (async () => {
      try {
        const [p, c, ci] = await Promise.all([
          axios.get(`${API}/b2b/me/produits`, { headers }),
          axios.get(`${API}/b2b/me/commandes`, { headers }),
          axios.get(`${API}/public/b2b/contact-info`),
        ]);
        setProduits(p.data.produits || []);
        setCommandes(c.data || []);
        setContactInfo(ci.data || {});
      } catch (e) {
        if (e?.response?.status === 401) {
          localStorage.clear();
          nav("/b2b-login");
        }
      }
    })();
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    localStorage.removeItem("b2b_token");
    localStorage.removeItem("b2b_ent");
    localStorage.removeItem("b2b_user");
    nav("/b2b-login");
  };

  const filteredProduits = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    return produits.filter(p => !q || (`${p.modele} ${p.couleur} ${p.sku}`).toLowerCase().includes(q));
  }, [produits, productQuery]);

  const addToCart = (p) => {
    setCart((prev) => {
      const idx = prev.findIndex(x => x.sku === p.sku);
      const stockLimit = p.en_precommande ? 9999 : Math.max(0, p.stock_restant || 0);
      if (idx >= 0) {
        if (prev[idx].quantite >= stockLimit) {
          toast.error(`Stock max atteint (${stockLimit} disponible${stockLimit > 1 ? "s" : ""})`);
          return prev;
        }
        const next = [...prev];
        next[idx] = { ...next[idx], quantite: next[idx].quantite + 1, stock_max: stockLimit };
        return next;
      }
      if (stockLimit <= 0) {
        toast.error("Rupture de stock");
        return prev;
      }
      return [...prev, { sku: p.sku, modele: p.modele, couleur: p.couleur, image_url: p.image_url || "", prix_ht: p.prix_b2b_final_ht || 0, quantite: 1, en_precommande: !!p.en_precommande, date_arrivee_estimee: p.date_arrivee_estimee || "", stock_max: stockLimit }];
    });
    toast.success(`${p.modele} ${p.couleur} ajouté`);
  };
  const changeQty = (sku, delta) => setCart((prev) => prev.map(it => {
    if (it.sku !== sku) return it;
    const max = it.stock_max ?? 9999;
    const newQty = it.quantite + delta;
    if (newQty < 1) return it;
    if (newQty > max) {
      toast.error(`Stock max atteint (${max})`);
      return it;
    }
    return { ...it, quantite: newQty };
  }));
  const setQty = (sku, value) => setCart((prev) => prev.map(it => {
    if (it.sku !== sku) return it;
    const max = it.stock_max ?? 9999;
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 1) return { ...it, quantite: 1 };
    if (parsed > max) {
      toast.error(`Stock max ${max}`);
      return { ...it, quantite: max };
    }
    return { ...it, quantite: parsed };
  }));
  const removeItem = (sku) => setCart((prev) => prev.filter(it => it.sku !== sku));

  // ── Autocomplétion clients privés du B2B ──
  const searchClients = async (q) => {
    setClientQuery(q);
    if (!q || q.length < 2) { setClientSuggestions([]); return; }
    try {
      const r = await axios.get(`${API}/b2b/me/clients?q=${encodeURIComponent(q)}`, { headers });
      setClientSuggestions(r.data.slice(0, 8));
    } catch { /* silent */ }
  };
  const pickClient = (c) => {
    setClientFinal({
      nom: c.nom || "",
      prenom: c.prenom || "",
      telephone: c.telephone || "",
      email: c.email || "",
      adresse: c.adresse || "",
      ville: c.ville || "",
      code_postal: c.code_postal || "",
    });
    setClientQuery("");
    setClientSuggestions([]);
    toast.success(`Client ${c.nom} chargé`);
  };
  const loadMesClients = async () => {
    try {
      const r = await axios.get(`${API}/b2b/me/clients`, { headers });
      setMesClients(r.data || []);
    } catch { /* silent */ }
  };

  const totalHT = cart.reduce((a, it) => a + it.prix_ht * it.quantite, 0);
  const totalQte = cart.reduce((a, it) => a + it.quantite, 0);

  // Étape 1 — Valider les infos et passer à l'écran de vérification (aucune commande créée)
  const goToVerify = () => {
    if (cart.length === 0) { toast.error("Panier vide"); return; }
    if (livrerChez === "b2b") {
      if (!orderForm.contact_nom?.trim() || !orderForm.contact_telephone?.trim() || !orderForm.adresse_livraison?.trim()) {
        toast.error("Coordonnées de livraison incomplètes (nom, téléphone, adresse)");
        return;
      }
    } else {
      if (!clientFinal.nom.trim() || !clientFinal.telephone.trim() || !clientFinal.adresse.trim()) {
        toast.error("Coordonnées client final incomplètes (nom, téléphone, adresse)");
        return;
      }
    }
    setCheckoutStep("verify");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Étape 2 — Confirmer réellement la commande (POST unique, anti-double-clic)
  const confirmOrder = async () => {
    if (submitting) return; // anti double-tap
    setSubmitting(true);
    try {
      const payload = {
        ...orderForm,
        produits: cart.map(it => ({ sku: it.sku, quantite: it.quantite })),
        livrer_chez: livrerChez,
        livraison_paye_par: livrerChez === "client_final" ? livraisonPayePar : "b2b",
        client_final: livrerChez === "client_final" ? clientFinal : null,
        mode_paiement_souhaite: modePaiement,
      };
      const res = await axios.post(`${API}/b2b/me/commandes`, payload, { headers });
      // Snapshot pour l'écran de confirmation (avant reset des états)
      setConfirmedOrder({
        numero_commande: res.data.numero_commande,
        id: res.data.id,
        cart_snapshot: [...cart],
        total_ht: totalHT,
        frais_livraison: livrerChez === "client_final" ? (fraisPreview?.frais ?? null) : 0,
        livrer_chez: livrerChez,
        client_final_snapshot: livrerChez === "client_final" ? { ...clientFinal } : null,
        contact_snapshot: livrerChez === "b2b" ? { ...orderForm } : null,
        mode_paiement: modePaiement,
      });
      // Reload commandes en arrière-plan
      axios.get(`${API}/b2b/me/commandes`, { headers }).then((c) => setCommandes(c.data || [])).catch(() => {});
      setCheckoutStep("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création de la commande");
    } finally { setSubmitting(false); }
  };

  // Retour depuis l'écran de vérification vers le formulaire (données conservées)
  const backToCart = () => { setCheckoutStep("cart"); window.scrollTo({ top: 0, behavior: "smooth" }); };

  // Après confirmation : "Nouvelle commande" — reset intégral + retour catalogue
  const startNewOrder = () => {
    setCart([]);
    setClientFinal({ nom: "", prenom: "", telephone: "", email: "", adresse: "", ville: "", code_postal: "" });
    setOrderForm({ contact_nom: user?.nom || "", contact_telephone: "", contact_email: user?.email || "", adresse_livraison: "", ville: "", code_postal: "", date_souhaitee: "", notes: "" });
    setLivrerChez("b2b");
    setLivraisonPayePar("b2b");
    setModePaiement("virement");
    setFraisPreview(null);
    setConfirmedOrder(null);
    setCheckoutStep("cart");
    setTab("catalogue");
  };

  // "Voir ma commande" → onglet Commandes + surligne la nouvelle commande via lastNumero
  const [highlightedCommande, setHighlightedCommande] = useState(null);
  useEffect(() => {
    if (!highlightedCommande) return;
    const t = setTimeout(() => setHighlightedCommande(null), 6000);
    return () => clearTimeout(t);
  }, [highlightedCommande]);
  const goToMyOrder = () => {
    if (confirmedOrder?.numero_commande) setHighlightedCommande(confirmedOrder.numero_commande);
    setCheckoutStep("cart");
    setTab("commandes");
  };

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/public/b2b/contact`, contactForm);
      toast.success("Message envoyé — nous vous recontactons rapidement");
      setContactForm({ ...contactForm, sujet: "", message: "" });
    } catch { toast.error("Erreur lors de l'envoi"); }
  };

  const saveProfil = async () => {
    try {
      await axios.put(`${API}/b2b/me/profil`, profil, { headers });
      const newUser = { ...user, ...profil };
      localStorage.setItem("b2b_user", JSON.stringify(newUser));
      toast.success("Profil mis à jour");
    } catch { toast.error("Erreur"); }
  };

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  if (!token) return null;

  // Sidebar desktop — mêmes clés que TABS (pas de nouvelles pages)
  const SIDEBAR_NAV = [
    { key: "catalogue", label: "Catalogue",     icon: Package,       section: "nav" },
    { key: "commandes", label: "Mes commandes", icon: ShoppingCart,  section: "nav" },
    { key: "clients",   label: "Mes clients",   icon: Users,         section: "nav" },
    { key: "profil",    label: "Profil",        icon: User,          section: "compte" },
    { key: "contact",   label: "Contact",       icon: Mail,          section: "support" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 lg:pl-[220px]">
      {/* Sidebar desktop — visible à partir de lg (>=1024px) */}
      <aside
        className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-[220px] bg-white border-r border-slate-200 z-40"
        data-testid="b2b-sidebar"
      >
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-slate-800 leading-tight">TopCocoon</p>
              <p className="text-xs text-slate-500 leading-tight">Portail B2B</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4">
          {["nav", "compte", "support"].map((sec, i) => (
            <div key={sec}>
              {sec === "compte" && <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Compte</p>}
              {sec === "support" && <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Support</p>}
              {i > 0 && sec === "nav" && <div className="border-t border-slate-100 my-2" />}
              {SIDEBAR_NAV.filter((t) => t.section === sec).map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => { setTab(t.key); if (t.key === "clients") loadMesClients(); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition ${active ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}
                    data-testid={`b2b-sidebar-${t.key}`}
                  >
                    <Icon className={`w-4 h-4 ${active ? "text-white" : "text-slate-400"}`} />
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100 space-y-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleDiscret}
            className={`w-full justify-start ${discret ? "bg-amber-50 border-amber-300 text-amber-800" : ""}`}
            data-testid="b2b-sidebar-discret"
          >
            {discret ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {discret ? "Mode présentation ON" : "Mode présentation"}
          </Button>
          <Button variant="outline" size="sm" onClick={logout} className="w-full justify-start" data-testid="b2b-sidebar-logout">
            <LogOut className="w-4 h-4 mr-2" />Déconnexion
          </Button>
        </div>
      </aside>

      {/* Bandeau discret — Mode présentation client (visible partout, très léger) */}
      {discret && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-[11px] py-1 px-4 text-center flex items-center justify-center gap-1.5" data-testid="b2b-presentation-banner">
          <EyeOff className="w-3 h-3" /> Mode présentation client — prix masqués
        </div>
      )}
      {/* Header — compact mobile, sans email */}
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-bold text-slate-800 text-sm sm:text-base truncate leading-tight">{ent?.nom}</p>
              <p className="text-[10px] sm:text-xs text-slate-500 truncate leading-tight">
                Compte professionnel{ent?.remise_pct > 0 ? ` · Remise ${ent.remise_pct}%` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {cart.length > 0 && (
              <span className="hidden sm:inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 rounded-full px-3 py-1.5 text-sm font-semibold" data-testid="b2b-espace-cart">
                <ShoppingCart className="w-4 h-4" />
                {totalQte}{discret ? "" : ` · ${totalHT.toFixed(0)} € HT`}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleDiscret}
              title={discret ? "Afficher les prix" : "Mode présentation client"}
              className={`h-9 px-2.5 ${discret ? "bg-amber-50 border-amber-300 text-amber-700" : ""}`}
              data-testid="b2b-discret-toggle"
            >
              {discret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span className="hidden md:inline ml-1">{discret ? "Discret" : "Prix"}</span>
            </Button>
            {/* Desktop lg+: logout in sidebar ; md-lg: header ; mobile: menu "Plus" */}
            <Button variant="outline" size="sm" onClick={logout} className="hidden md:inline-flex lg:hidden" data-testid="b2b-logout">
              <LogOut className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
        {/* Tabs — visibles à partir de md, masquées sur lg (sidebar prend le relais) */}
        <div className="max-w-[1600px] mx-auto px-4 hidden md:flex lg:hidden gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
              onClick={() => { setTab(t.key); if (t.key === "clients") loadMesClients(); }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${tab === t.key ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                data-testid={`b2b-tab-${t.key}`}
              >
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6 pb-24 md:pb-6" style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom))" }}>
        {tab === "catalogue" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={productQuery} onChange={(e) => setProductQuery(e.target.value)} placeholder="Rechercher…" className="pl-9 bg-white" data-testid="b2b-espace-search" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
                {filteredProduits.map((p) => {
                  const out = !p.en_precommande && p.stock_restant <= 0;
                  const badge = stockBadge(p);
                  const preDate = preorderDateLabel(p.date_arrivee_estimee);
                  const inCart = cart.find((c) => c.sku === p.sku);
                  return (
                    <div
                      key={p.id}
                      className={`relative flex flex-col rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm transition ${out ? "opacity-60" : "hover:shadow-md hover:border-indigo-300"}`}
                      data-testid={`b2b-espace-tile-${p.sku}`}
                    >
                      {p.en_precommande && (
                        <span className="absolute top-1.5 left-1.5 z-10 text-[10px] px-2 py-0.5 rounded-full bg-orange-500 text-white font-semibold shadow" data-testid={`b2b-precommande-badge-${p.sku}`}>
                          Précommande{preDate ? ` · ${preDate}` : ""}
                        </span>
                      )}
                      <button
                        type="button"
                        disabled={out}
                        onClick={() => { setDetailProduct(p); setDetailImgIdx(0); }}
                        className="text-left w-full disabled:cursor-not-allowed"
                        data-testid={`b2b-espace-tile-open-${p.sku}`}
                      >
                        <div className="aspect-square w-full bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center overflow-hidden">
                          {p.image_url ? (
                            <img src={p.image_url} alt={p.modele} loading="lazy" className="w-full h-full object-cover" />
                          ) : (<ImageIcon className="w-10 h-10 text-slate-300" />)}
                        </div>
                        <div className="px-2.5 pt-2 pb-1">
                          <p className="font-semibold text-sm text-slate-800 leading-tight line-clamp-1">{p.modele}</p>
                          <p className="text-xs text-slate-500 leading-tight line-clamp-1">{p.couleur}</p>
                          <div className="flex items-center justify-between gap-1.5 mt-1">
                            {!p.en_precommande ? (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${badge.cls}`} data-testid={`b2b-stock-badge-${p.sku}`}>
                                {badge.label}
                              </span>
                            ) : <span />}
                            {!discret && (
                              <span className="text-sm font-bold text-indigo-600 whitespace-nowrap" data-testid={`b2b-price-${p.sku}`}>
                                {(p.prix_b2b_final_ht || 0).toFixed(2)} €
                              </span>
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Actions bas — quantité + panier (aligné en bas de carte) */}
                      <div className="mt-auto p-2 pt-0" onClick={(e) => e.stopPropagation()}>
                        {inCart ? (
                          <div className="flex items-center gap-1 bg-indigo-50 rounded-lg p-1" data-testid={`b2b-card-qty-${p.sku}`}>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 flex-shrink-0 hover:bg-white" onClick={() => changeQty(p.sku, -1)} data-testid={`b2b-card-minus-${p.sku}`}>
                              <Minus className="w-3.5 h-3.5" />
                            </Button>
                            <span className="flex-1 text-center text-sm font-bold text-indigo-700" data-testid={`b2b-card-qty-value-${p.sku}`}>{inCart.quantite}</span>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 flex-shrink-0 hover:bg-white" onClick={() => changeQty(p.sku, 1)} data-testid={`b2b-card-plus-${p.sku}`}>
                              <Plus className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            disabled={out}
                            onClick={() => addToCart(p)}
                            className="w-full h-8 text-xs bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                            data-testid={`b2b-card-add-${p.sku}`}
                          >
                            <ShoppingCart className="w-3.5 h-3.5 mr-1" />
                            {out ? "Rupture" : "Ajouter"}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
              {checkoutStep === "cart" && (<>
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <p className="font-semibold text-slate-800">Votre commande</p>
                  {cart.length === 0 ? (
                    <p className="text-slate-400 text-sm text-center py-4">Cliquez sur un produit pour l&apos;ajouter.</p>
                  ) : (
                    <div className="space-y-2.5 max-h-80 overflow-y-auto" data-testid="b2b-cart-items">
                      {cart.map((it) => (
                        <div key={it.sku} className="flex gap-2.5 bg-slate-50 rounded-lg p-2.5" data-testid={`b2b-cart-line-${it.sku}`}>
                          {/* Photo produit */}
                          <div className="w-14 h-14 flex-shrink-0 rounded-md overflow-hidden bg-white border">
                            {it.image_url ? (
                              <img src={it.image_url} alt={it.modele} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-6 h-6 text-slate-300" /></div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-1">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-slate-800 truncate leading-tight">{it.modele}</p>
                                <p className="text-xs text-slate-500 truncate leading-tight">{it.couleur}</p>
                                {it.en_precommande && (
                                  <div className="mt-1">
                                    <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold" data-testid={`b2b-cart-precommande-${it.sku}`}>Précommande</span>
                                    <p className="text-[10px] text-orange-700 mt-0.5">
                                      {preorderDateLabel(it.date_arrivee_estimee)
                                        ? `Disponibilité estimée : ${preorderDateLabel(it.date_arrivee_estimee)}`
                                        : "Produit en précommande"}
                                    </p>
                                  </div>
                                )}
                              </div>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 -mr-1 text-slate-400 hover:text-red-500 hover:bg-red-50" onClick={() => removeItem(it.sku)} data-testid={`b2b-cart-remove-${it.sku}`}>
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            {!discret && (
                              <p className="text-[11px] text-slate-500 mt-0.5">{it.prix_ht.toFixed(2)} € HT × {it.quantite}</p>
                            )}
                            <div className="flex items-center justify-between gap-2 mt-1.5">
                              <div className="flex items-center gap-1">
                                <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(it.sku, -1)} data-testid={`b2b-cart-minus-${it.sku}`}><Minus className="w-3 h-3" /></Button>
                                <Input
                                  type="number"
                                  min={1}
                                  max={it.stock_max ?? 9999}
                                  value={it.quantite}
                                  onChange={(e) => setQty(it.sku, e.target.value)}
                                  className="w-12 h-7 text-center text-sm font-semibold px-1"
                                  data-testid={`b2b-cart-qty-${it.sku}`}
                                />
                                <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(it.sku, 1)} data-testid={`b2b-cart-plus-${it.sku}`}><Plus className="w-3 h-3" /></Button>
                              </div>
                              {!discret && (
                                <span className="text-sm font-bold text-indigo-700 whitespace-nowrap" data-testid={`b2b-cart-line-total-${it.sku}`}>{(it.prix_ht * it.quantite).toFixed(2)} € HT</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Récap panier — sous-total / livraison / total HT */}
                  {cart.length > 0 && !discret && (
                    <div className="border-t pt-3 space-y-1.5" data-testid="b2b-cart-recap">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Sous-total produits</span>
                        <span className="text-slate-800 font-medium">{totalHT.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Livraison</span>
                        {(() => {
                          if (livrerChez !== "client_final") return <span className="text-slate-800 font-medium">0,00 €</span>;
                          if (fraisPreview?.frais != null) return <span className="text-slate-800 font-medium" data-testid="b2b-cart-frais">{fraisPreview.frais.toFixed(2)} €</span>;
                          return <span className="text-amber-700 font-medium text-xs" data-testid="b2b-cart-frais-tbd">À confirmer par TopCocoon</span>;
                        })()}
                      </div>
                      <div className="border-t pt-1.5 flex justify-between items-baseline">
                        <span className="text-slate-700 text-sm font-semibold">
                          Total HT{livrerChez === "client_final" && fraisPreview?.frais == null ? " provisoire" : ""}
                        </span>
                        <span className="text-xl font-bold text-indigo-700" data-testid="b2b-espace-total">
                          {(totalHT + (livrerChez === "client_final" ? (fraisPreview?.frais || 0) : 0)).toFixed(2)} €
                        </span>
                      </div>
                    </div>
                  )}
                  {/* Bandeau info stock/précommande mixte */}
                  {cart.length > 0 && cart.some(it => it.en_precommande) && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5 text-[11px] text-orange-900 flex gap-2" data-testid="b2b-cart-mix-banner">
                      <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        {cart.some(it => !it.en_precommande) ? (
                          <>Votre commande contient des articles disponibles <strong>et</strong> des articles en précommande.<br />TopCocoon confirmera la date de livraison après validation.</>
                        ) : (
                          <>Cette commande ne contient que des produits en précommande.<br />La date de livraison sera confirmée par TopCocoon.</>
                        )}
                      </div>
                    </div>
                  )}

                  {/* CTA principal — révèle le formulaire livraison/paiement */}
                  {cart.length > 0 && !showCheckoutForm && (
                    <Button
                      onClick={() => setShowCheckoutForm(true)}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 h-11 mt-1"
                      data-testid="b2b-espace-passer-commande"
                    >
                      Passer la commande<ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Livrer chez qui ? — Affiché uniquement après "Passer la commande" */}
              {showCheckoutForm && (
              <>
              <button
                type="button"
                onClick={() => setShowCheckoutForm(false)}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                data-testid="b2b-back-to-cart"
              >
                <ChevronLeft className="w-4 h-4" />Modifier le panier
              </button>
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <p className="font-semibold text-slate-800 text-sm">Livraison au client final</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setLivrerChez("b2b")}
                      className={`p-3 rounded-lg border-2 text-left transition ${livrerChez === "b2b" ? "border-indigo-600 bg-indigo-50" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid="b2b-livrer-chez-b2b"
                    >
                      <Home className="w-5 h-5 text-indigo-600 mb-1" />
                      <p className="text-xs font-semibold">Chez moi</p>
                      <p className="text-[10px] text-slate-500">Livrer à mon adresse</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setLivrerChez("client_final")}
                      className={`p-3 rounded-lg border-2 text-left transition ${livrerChez === "client_final" ? "border-indigo-600 bg-indigo-50" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid="b2b-livrer-chez-client"
                    >
                      <UserCheck className="w-5 h-5 text-indigo-600 mb-1" />
                      <p className="text-xs font-semibold">Chez mon client</p>
                      <p className="text-[10px] text-slate-500">Livraison directe au client final</p>
                    </button>
                  </div>

                  {livrerChez === "b2b" ? (
                    <>
                      <Input placeholder="Contact" value={orderForm.contact_nom} onChange={(e) => setOrderForm({ ...orderForm, contact_nom: e.target.value })} data-testid="b2b-espace-order-contact" />
                      <Input placeholder="Téléphone" value={orderForm.contact_telephone} onChange={(e) => setOrderForm({ ...orderForm, contact_telephone: e.target.value })} />
                      <Input placeholder="Adresse" value={orderForm.adresse_livraison} onChange={(e) => setOrderForm({ ...orderForm, adresse_livraison: e.target.value })} />
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="CP" value={orderForm.code_postal} onChange={(e) => setOrderForm({ ...orderForm, code_postal: e.target.value })} />
                        <Input placeholder="Ville" value={orderForm.ville} onChange={(e) => setOrderForm({ ...orderForm, ville: e.target.value })} />
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2 border-t pt-3">
                      {/* Encart info livraison */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-xs text-blue-900" data-testid="b2b-info-livraison-samedi">
                        <div className="font-semibold flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Livraisons directes chez le client : uniquement le samedi</div>
                        <div className="text-blue-700 mt-1">📱 Votre client recevra un SMS 24h avant pour organiser le créneau.</div>
                      </div>
                      <p className="text-xs font-medium text-slate-600">Client final</p>
                      {/* Autocomplétion */}
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          placeholder="Rechercher un client existant…"
                          value={clientQuery}
                          onChange={(e) => searchClients(e.target.value)}
                          className="pl-8"
                          data-testid="b2b-client-search"
                        />
                        {clientSuggestions.length > 0 && (
                          <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {clientSuggestions.map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => pickClient(c)}
                                className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-xs border-b last:border-0"
                                data-testid={`b2b-client-suggest-${c.id}`}
                              >
                                <div className="font-semibold text-slate-700">{c.prenom} {c.nom}</div>
                                <div className="text-slate-400">{c.telephone} · {c.adresse}, {c.ville}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Prénom" value={clientFinal.prenom} onChange={(e) => setClientFinal({ ...clientFinal, prenom: e.target.value })} data-testid="b2b-cf-prenom" />
                        <Input placeholder="Nom *" value={clientFinal.nom} onChange={(e) => setClientFinal({ ...clientFinal, nom: e.target.value })} data-testid="b2b-cf-nom" />
                      </div>
                      <Input placeholder="Téléphone *" value={clientFinal.telephone} onChange={(e) => setClientFinal({ ...clientFinal, telephone: e.target.value })} data-testid="b2b-cf-tel" />
                      <Input placeholder="Email (optionnel)" value={clientFinal.email} onChange={(e) => setClientFinal({ ...clientFinal, email: e.target.value })} />
                      <Input placeholder="Adresse *" value={clientFinal.adresse} onChange={(e) => setClientFinal({ ...clientFinal, adresse: e.target.value })} data-testid="b2b-cf-adresse" />
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="CP" value={clientFinal.code_postal} onChange={(e) => setClientFinal({ ...clientFinal, code_postal: e.target.value })} />
                        <Input placeholder="Ville" value={clientFinal.ville} onChange={(e) => setClientFinal({ ...clientFinal, ville: e.target.value })} />
                      </div>
                      {/* Preview frais livraison temps réel */}
                      {fraisPreview && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs" data-testid="b2b-frais-preview">
                          <div className="flex justify-between items-baseline">
                            <span className="text-emerald-900 font-semibold">📦 Frais de livraison estimés</span>
                            <span className="text-lg font-bold text-emerald-700">{fraisPreview.frais.toFixed(2)} €</span>
                          </div>
                          <div className="text-[10px] text-emerald-600 mt-0.5">Zone : {fraisPreview.zone} — CP {fraisPreview.code_postal}</div>
                          {livraisonPayePar === "client_final" && (
                            <div className="text-[10px] text-emerald-700 font-medium mt-1">→ À annoncer à votre client (livreur encaissera sur place)</div>
                          )}
                        </div>
                      )}

                      {/* Qui paie la livraison ? */}
                      <div className="border-t pt-3">
                        <p className="text-xs font-medium text-slate-600 mb-1.5">Frais de livraison payés par</p>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setLivraisonPayePar("b2b")}
                            className={`p-2 rounded-lg border-2 text-left text-xs transition ${livraisonPayePar === "b2b" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200"}`}
                            data-testid="b2b-liv-pay-b2b"
                          >
                            <strong>Moi (B2B)</strong>
                            <div className="text-[10px] text-slate-500">Ajouté à ma facture</div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setLivraisonPayePar("client_final")}
                            className={`p-2 rounded-lg border-2 text-left text-xs transition ${livraisonPayePar === "client_final" ? "border-amber-500 bg-amber-50 text-amber-800" : "border-slate-200"}`}
                            data-testid="b2b-liv-pay-client"
                          >
                            <strong>Mon client</strong>
                            <div className="text-[10px] text-slate-500">Le livreur encaisse chez le client</div>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-[11px] font-medium text-slate-600 mb-1 block">Date de livraison souhaitée</label>
                    <Input type="date" value={orderForm.date_souhaitee} onChange={(e) => setOrderForm({ ...orderForm, date_souhaitee: e.target.value })} data-testid="b2b-order-date" />
                    {(() => {
                      // Avertissement si la date souhaitée est avant la disponibilité estimée d'une précommande
                      if (!orderForm.date_souhaitee) return null;
                      const wanted = new Date(orderForm.date_souhaitee);
                      const late = cart.filter(it => it.en_precommande && it.date_arrivee_estimee && new Date(it.date_arrivee_estimee) > wanted);
                      if (late.length === 0) return null;
                      const first = late[0];
                      return (
                        <div className="mt-1.5 bg-amber-50 border border-amber-200 text-amber-900 text-[11px] rounded-lg p-2 flex gap-2" data-testid="b2b-cart-date-warning">
                          <Clock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span>« {first.modele} » est en précommande, disponibilité estimée au <strong>{preorderDateLabel(first.date_arrivee_estimee)}</strong>. La date de livraison pourra être ajustée après validation par TopCocoon.</span>
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 mb-1 block">Instructions de livraison</label>
                    <Textarea placeholder="Créneau souhaité, code d'immeuble, étage, instructions livreur…" rows={2} value={orderForm.notes} onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })} data-testid="b2b-order-notes" />
                  </div>
                  {livrerChez === "client_final" && (
                    <div>
                      <label className="text-[11px] font-medium text-slate-600 mb-1 block">Référence interne partenaire (optionnel)</label>
                      <Input
                        placeholder="CLIENT-DUPONT-025"
                        value={orderForm.reference_partenaire || ""}
                        onChange={(e) => setOrderForm({ ...orderForm, reference_partenaire: e.target.value })}
                        data-testid="b2b-order-ref-partenaire"
                      />
                    </div>
                  )}

                  {/* Livraison neutre / marque blanche */}
                  {livrerChez === "client_final" && (
                    <div className="border-t pt-3">
                      <label className="flex items-start gap-2.5 cursor-pointer" data-testid="b2b-livraison-neutre-toggle">
                        <input
                          type="checkbox"
                          checked={!!orderForm.livraison_neutre}
                          onChange={(e) => setOrderForm({ ...orderForm, livraison_neutre: e.target.checked })}
                          className="mt-0.5 w-4 h-4"
                        />
                        <span className="text-xs">
                          <span className="font-semibold text-slate-800">Livraison neutre / marque blanche</span>
                          <span className="block text-slate-500 mt-0.5 leading-snug">Aucun prix d&apos;achat B2B ni document commercial TopCocoon ne sera communiqué à votre client final.</span>
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Mode de paiement souhaité */}
                  <div className="pt-2">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Mode de paiement souhaité</p>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setModePaiement("virement")}
                        className={`p-2 rounded-lg border-2 text-center text-xs transition ${modePaiement === "virement" ? "border-indigo-600 bg-indigo-50 text-indigo-800" : "border-slate-200 hover:border-slate-300"}`}
                        data-testid="b2b-mode-virement"
                      >
                        <div className="text-lg">🏦</div>
                        <div className="font-semibold">Virement</div>
                        <div className="text-[10px] text-slate-500">Sous 48h</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setModePaiement("especes")}
                        className={`p-2 rounded-lg border-2 text-center text-xs transition ${modePaiement === "especes" ? "border-amber-500 bg-amber-50 text-amber-800" : "border-slate-200 hover:border-slate-300"}`}
                        data-testid="b2b-mode-especes"
                      >
                        <div className="text-lg">💵</div>
                        <div className="font-semibold">Cash</div>
                        <div className="text-[10px] text-slate-500">À la livraison</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setModePaiement("stripe")}
                        className={`p-2 rounded-lg border-2 text-center text-xs transition ${modePaiement === "stripe" ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 hover:border-slate-300"}`}
                        data-testid="b2b-mode-stripe"
                      >
                        <div className="text-lg">💳</div>
                        <div className="font-semibold">CB en ligne</div>
                        <div className="text-[10px] text-slate-500">Stripe sécurisé</div>
                      </button>
                    </div>
                    {modePaiement === "stripe" && (
                      <p className="text-[11px] text-emerald-700 mt-2">Un lien de paiement Stripe te sera envoyé après validation de la commande.</p>
                    )}
                  </div>

                  <Button onClick={goToVerify} disabled={cart.length === 0} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" data-testid="b2b-espace-continue">
                    <ChevronRight className="w-4 h-4 mr-1" />Continuer
                  </Button>
                  <p className="text-xs text-slate-400 text-center">Vous pourrez vérifier votre commande avant confirmation.</p>
                </CardContent>
              </Card>
              </>)}
              </>)}

              {/* ---- ÉTAPE VÉRIFICATION ---- */}
              {checkoutStep === "verify" && (
                <Card data-testid="b2b-verify-screen">
                  <CardContent className="pt-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <button onClick={backToCart} className="text-slate-500 hover:text-slate-700 p-1" data-testid="b2b-verify-back"><ChevronLeft className="w-5 h-5" /></button>
                      <h2 className="text-lg font-bold text-slate-800">Vérifiez votre commande</h2>
                    </div>

                    {/* PRODUITS */}
                    <div>
                      <p className="text-[11px] uppercase font-bold text-slate-500 tracking-wide mb-2">Produits</p>
                      <div className="space-y-2 border rounded-lg p-2.5 bg-slate-50">
                        {cart.map((it) => (
                          <div key={it.sku} className="flex justify-between items-start gap-2 text-sm">
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800">{it.quantite} × {it.modele} <span className="text-slate-500 font-normal">— {it.couleur}</span></p>
                              {it.en_precommande && (
                                <p className="text-[11px] text-orange-700 mt-0.5">
                                  <span className="inline-block bg-orange-100 rounded-full px-1.5 py-0.5 font-semibold mr-1">Précommande</span>
                                  {preorderDateLabel(it.date_arrivee_estimee) ? `Arrivage prévu : ${preorderDateLabel(it.date_arrivee_estimee)}` : "Date d'arrivée à confirmer"}
                                </p>
                              )}
                              {!discret && !it.en_precommande && <p className="text-xs text-slate-500">{it.prix_ht.toFixed(2)} € HT / u</p>}
                            </div>
                            {!discret && <span className="text-sm font-bold text-slate-700 whitespace-nowrap">{(it.prix_ht * it.quantite).toFixed(2)} €</span>}
                          </div>
                        ))}
                      </div>
                      {cart.some(it => it.en_precommande) && (
                        <div className="mt-2 bg-orange-50 border border-orange-200 rounded-lg p-2.5 text-[11px] text-orange-900 flex gap-2" data-testid="b2b-verify-precommande-banner">
                          <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>Cette commande contient des produits en précommande. La date définitive de livraison sera confirmée par TopCocoon.</span>
                        </div>
                      )}
                    </div>

                    {/* Récap prix */}
                    {!discret && (
                      <div className="space-y-1.5 border rounded-lg p-3">
                        <div className="flex justify-between text-sm"><span className="text-slate-600">Sous-total produits</span><span className="font-medium">{totalHT.toFixed(2)} €</span></div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Livraison</span>
                          {(() => {
                            if (livrerChez !== "client_final") return <span className="font-medium">0,00 €</span>;
                            if (fraisPreview?.frais != null) return <span className="font-medium">{fraisPreview.frais.toFixed(2)} €</span>;
                            return <span className="text-amber-700 font-medium text-xs">À confirmer par TopCocoon</span>;
                          })()}
                        </div>
                        <div className="border-t pt-1.5 flex justify-between items-baseline">
                          <span className="text-sm font-semibold">Total HT{livrerChez === "client_final" && fraisPreview?.frais == null ? " provisoire" : ""}</span>
                          <span className="text-xl font-bold text-indigo-700">{(totalHT + (livrerChez === "client_final" ? (fraisPreview?.frais || 0) : 0)).toFixed(2)} €</span>
                        </div>
                      </div>
                    )}

                    {/* CLIENT FINAL ou CONTACT B2B */}
                    <div>
                      <p className="text-[11px] uppercase font-bold text-slate-500 tracking-wide mb-2">{livrerChez === "client_final" ? "Client final" : "Livraison chez vous"}</p>
                      <div className="border rounded-lg p-3 text-sm space-y-0.5">
                        {livrerChez === "client_final" ? (<>
                          <p className="font-semibold text-slate-800">{clientFinal.prenom} {clientFinal.nom}</p>
                          <p className="text-slate-600">{clientFinal.telephone}</p>
                          {clientFinal.email && <p className="text-slate-600">{clientFinal.email}</p>}
                          <p className="text-slate-600 pt-1 border-t mt-1">{clientFinal.adresse}</p>
                          <p className="text-slate-600">{clientFinal.code_postal} {clientFinal.ville}</p>
                        </>) : (<>
                          <p className="font-semibold text-slate-800">{orderForm.contact_nom}</p>
                          <p className="text-slate-600">{orderForm.contact_telephone}</p>
                          <p className="text-slate-600 pt-1 border-t mt-1">{orderForm.adresse_livraison}</p>
                          <p className="text-slate-600">{orderForm.code_postal} {orderForm.ville}</p>
                        </>)}
                      </div>
                    </div>

                    {/* Infos livraison */}
                    <div className="border rounded-lg p-3 text-sm space-y-1">
                      {orderForm.date_souhaitee && (
                        <div className="flex gap-2"><Calendar className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" /><div><p className="text-[11px] text-slate-500">Date souhaitée</p><p className="text-slate-800">{new Date(orderForm.date_souhaitee).toLocaleDateString("fr-BE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p></div></div>
                      )}
                      {orderForm.notes && (
                        <div className="flex gap-2 pt-1"><FileText className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" /><div><p className="text-[11px] text-slate-500">Instructions</p><p className="text-slate-800 whitespace-pre-wrap">{orderForm.notes}</p></div></div>
                      )}
                      {livrerChez === "client_final" && orderForm.reference_partenaire && (
                        <div className="flex gap-2 pt-1"><Package className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" /><div><p className="text-[11px] text-slate-500">Référence interne</p><p className="text-slate-800 font-mono">{orderForm.reference_partenaire}</p></div></div>
                      )}
                    </div>

                    {/* OPTIONS */}
                    <div>
                      <p className="text-[11px] uppercase font-bold text-slate-500 tracking-wide mb-2">Options</p>
                      <div className="space-y-1.5">
                        {livrerChez === "client_final" && (
                          <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Livraison chez le client final</div>
                        )}
                        {livrerChez === "client_final" && orderForm.livraison_neutre && (
                          <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Livraison neutre / marque blanche</div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          Paiement : {modePaiement === "virement" ? "Virement" : modePaiement === "especes" ? "Cash à la livraison" : "CB en ligne (Stripe)"}
                        </div>
                      </div>
                    </div>

                    {/* Boutons */}
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                      <Button variant="outline" onClick={backToCart} disabled={submitting} className="h-11" data-testid="b2b-verify-edit">
                        Modifier
                      </Button>
                      <Button onClick={confirmOrder} disabled={submitting} className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="b2b-verify-confirm">
                        {submitting ? "Envoi…" : (<><CheckCircle2 className="w-4 h-4 mr-1.5" />Confirmer la commande</>)}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* ---- ÉTAPE SUCCÈS ---- */}
              {checkoutStep === "success" && confirmedOrder && (
                <Card className="border-emerald-200 shadow-lg" data-testid="b2b-success-screen">
                  <CardContent className="pt-6 pb-5 space-y-4">
                    <div className="text-center">
                      <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                      </div>
                      <h2 className="text-lg font-bold text-slate-800">Commande #{confirmedOrder.numero_commande} reçue</h2>
                      <p className="text-sm text-slate-600 mt-1">Votre commande a bien été transmise à TopCocoon.</p>
                    </div>

                    <div className="border rounded-lg p-3 space-y-2 text-sm">
                      {confirmedOrder.cart_snapshot.map((it) => (
                        <div key={it.sku} className="flex justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800">{it.quantite} × {it.modele}</p>
                            <p className="text-xs text-slate-500">{it.couleur}</p>
                            {it.en_precommande && (
                              <p className="text-[11px] text-orange-700 mt-0.5">
                                <span className="inline-block bg-orange-100 rounded-full px-1.5 py-0.5 font-semibold mr-1">Précommande</span>
                                {preorderDateLabel(it.date_arrivee_estimee) ? `Arrivage prévu : ${preorderDateLabel(it.date_arrivee_estimee)}` : ""}
                              </p>
                            )}
                          </div>
                          {!discret && <span className="font-bold text-slate-700 whitespace-nowrap">{(it.prix_ht * it.quantite).toFixed(2)} € HT</span>}
                        </div>
                      ))}
                      {!discret && (
                        <div className="border-t pt-2 flex justify-between text-sm font-semibold">
                          <span>Total HT{confirmedOrder.livrer_chez === "client_final" && confirmedOrder.frais_livraison == null ? " provisoire" : ""}</span>
                          <span className="text-indigo-700">{(confirmedOrder.total_ht + (confirmedOrder.frais_livraison || 0)).toFixed(2)} €</span>
                        </div>
                      )}
                    </div>

                    <div className="border rounded-lg p-3 text-sm space-y-1.5">
                      {confirmedOrder.client_final_snapshot ? (<>
                        <div><p className="text-[11px] text-slate-500">Client</p><p className="font-semibold text-slate-800">{confirmedOrder.client_final_snapshot.prenom} {confirmedOrder.client_final_snapshot.nom}</p></div>
                        <div><p className="text-[11px] text-slate-500">Téléphone</p><p className="text-slate-700">{confirmedOrder.client_final_snapshot.telephone}</p></div>
                        <div><p className="text-[11px] text-slate-500">Livraison</p><p className="text-slate-700">{confirmedOrder.client_final_snapshot.adresse}<br />{confirmedOrder.client_final_snapshot.code_postal} {confirmedOrder.client_final_snapshot.ville}</p></div>
                      </>) : (<>
                        <div><p className="text-[11px] text-slate-500">Contact</p><p className="font-semibold text-slate-800">{confirmedOrder.contact_snapshot?.contact_nom}</p></div>
                        <div><p className="text-[11px] text-slate-500">Livraison</p><p className="text-slate-700">{confirmedOrder.contact_snapshot?.adresse_livraison}<br />{confirmedOrder.contact_snapshot?.code_postal} {confirmedOrder.contact_snapshot?.ville}</p></div>
                      </>)}
                      <div><p className="text-[11px] text-slate-500">Statut</p><span className="inline-block text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-semibold">En attente de validation</span></div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
                      <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>Vous serez informé dès que TopCocoon aura validé votre commande.</span>
                    </div>

                    {confirmedOrder.cart_snapshot.some(it => it.en_precommande) && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-900 flex items-start gap-2" data-testid="b2b-success-precommande-banner">
                        <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Votre commande contient un ou plusieurs produits en précommande. TopCocoon vous confirmera la disponibilité et la date de livraison.</span>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <Button variant="outline" onClick={goToMyOrder} className="h-11" data-testid="b2b-success-view-order">
                        Voir ma commande
                      </Button>
                      <Button onClick={startNewOrder} className="h-11 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="b2b-success-new-order">
                        Nouvelle commande
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {tab === "clients" && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-slate-800">Ma base clients ({mesClients.length})</p>
                <Button size="sm" variant="outline" onClick={loadMesClients}>Actualiser</Button>
              </div>
              {mesClients.length === 0 ? (
                <p className="text-slate-400 text-sm">Aucun client encore. Ils apparaîtront ici dès votre première commande « chez le client final ».</p>
              ) : (
                <div className="space-y-2">
                  {mesClients.map((c) => (
                    <div key={c.id} className="border rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`b2b-mes-client-${c.id}`}>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm">{c.prenom} {c.nom}</p>
                        <p className="text-xs text-slate-500">{c.telephone} · {c.email || "—"}</p>
                        <p className="text-xs text-slate-400 truncate">{c.adresse}, {c.code_postal} {c.ville}</p>
                      </div>
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <div className="text-right">
                          <div className="text-xs text-slate-500">{c.nombre_commandes || 1} cmd</div>
                          <div className="text-[10px] text-slate-400">{c.derniere_commande ? new Date(c.derniere_commande).toLocaleDateString("fr-BE") : "—"}</div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          data-testid={`b2b-delete-client-${c.id}`}
                          onClick={async () => {
                            if (!window.confirm(`Supprimer ${c.prenom || ""} ${c.nom} ?\n\nCette action est définitive.`)) return;
                            try {
                              await axios.delete(`${API}/b2b/me/clients/${c.id}`, { headers });
                              toast.success("Client supprimé");
                              setMesClients((prev) => prev.filter((x) => x.id !== c.id));
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Erreur");
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "commandes" && (
          <Card>
            <CardContent className="pt-4">
              <p className="font-semibold text-slate-800 mb-3">Historique des commandes ({commandes.length})</p>
              {commandes.length === 0 ? (
                <p className="text-slate-400 text-sm">Aucune commande pour l&apos;instant.</p>
              ) : (
                <div className="space-y-2">
                  {commandes.map((c) => {
                    const st = STATUT_LABELS[c.statut] || { label: c.statut, color: "bg-slate-100 text-slate-700", Icon: Clock };
                    const Icon = st.Icon;
                    const isHl = highlightedCommande && String(c.numero_commande) === String(highlightedCommande);
                    return (
                      <div key={c.id} className={`border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition ${isHl ? "border-emerald-400 bg-emerald-50/50 ring-2 ring-emerald-200" : ""}`} data-testid={`b2b-commande-${c.id}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">#{c.numero_commande || "—"}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${st.color}`}>
                              <Icon className="w-3 h-3" />{st.label}
                            </span>
                            {c.statut_paiement === "paye" && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Payé
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 truncate mt-0.5">{c.details_commande}</p>
                          <p className="text-xs text-slate-400">{(c.date_creation || "").slice(0, 10)}</p>
                        </div>
                        <div className="flex items-center gap-2 whitespace-nowrap flex-wrap justify-end">
                          {!discret && (
                            <span className="text-lg font-bold text-indigo-700">{(c.prix || 0).toFixed(2)} €</span>
                          )}
                          {c.statut_paiement !== "paye" && !c.paiement_recu_admin && ["en_attente_validation", "en_attente"].includes(c.statut) && (
                            <Button
                              size="sm"
                              onClick={() => payerCommande(c.id)}
                              disabled={payingId === c.id}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                              data-testid={`b2b-pay-${c.id}`}
                            >
                              <CreditCard className="w-3 h-3 mr-1" />{payingId === c.id ? "…" : (discret ? "Payer" : `Payer ${(c.prix || 0).toFixed(0)} €`)}
                            </Button>
                          )}
                          {(c.paiement_recu_admin || c.statut_paiement === "paye") && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-indigo-600 hover:bg-indigo-50 h-8"
                              onClick={() => openRecuModal(c)}
                              data-testid={`b2b-recu-${c.id}`}
                              title="Télécharger le reçu client (whitelabel)"
                            >
                              <FileText className="w-3 h-3 mr-1" />Reçu
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "profil" && (
          <Card className="max-w-md">
            <CardContent className="pt-4 space-y-3">
              <p className="font-semibold text-slate-800">Mon profil</p>
              <div><Label>Email</Label><Input value={user?.email || ""} disabled /></div>
              <div><Label>Nom</Label><Input value={profil.nom} onChange={(e) => setProfil({ ...profil, nom: e.target.value })} data-testid="b2b-profil-nom" /></div>
              <div><Label>Téléphone</Label><Input value={profil.telephone} onChange={(e) => setProfil({ ...profil, telephone: e.target.value })} data-testid="b2b-profil-tel" /></div>
              <Button onClick={saveProfil} className="bg-indigo-600 hover:bg-indigo-700" data-testid="b2b-profil-save">Enregistrer</Button>
              <p className="text-xs text-slate-400">Pour modifier votre email ou société, contactez TopCocoon.</p>
            </CardContent>
          </Card>
        )}

        {tab === "contact" && (
          <Card className="max-w-2xl">
            <CardContent className="pt-4 space-y-4">
              <p className="font-semibold text-slate-800">Nous contacter</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-2 text-slate-600"><Mail className="w-4 h-4 text-indigo-600" /> {contactInfo.email || "—"}</div>
                <div className="flex items-center gap-2 text-slate-600"><Phone className="w-4 h-4 text-indigo-600" /> {contactInfo.telephone || "—"}</div>
                <div className="flex items-center gap-2 text-slate-600"><Building2 className="w-4 h-4 text-indigo-600" /> {contactInfo.adresse || "—"}</div>
              </div>
              <form onSubmit={submitContact} className="space-y-3 border-t pt-4">
                <p className="font-medium text-slate-700 text-sm">Envoyer un message</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input placeholder="Nom" required value={contactForm.nom} onChange={(e) => setContactForm({ ...contactForm, nom: e.target.value })} />
                  <Input placeholder="Email" type="email" required value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
                  <Input placeholder="Téléphone (optionnel)" value={contactForm.telephone} onChange={(e) => setContactForm({ ...contactForm, telephone: e.target.value })} />
                  <Input placeholder="Sujet" value={contactForm.sujet} onChange={(e) => setContactForm({ ...contactForm, sujet: e.target.value })} />
                </div>
                <Textarea placeholder="Message *" rows={5} required value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} data-testid="b2b-contact-message" />
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="b2b-contact-submit">
                  <Send className="w-4 h-4 mr-2" />Envoyer
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal — Détail produit avec galerie */}
      {detailProduct && (() => {
        const p = detailProduct;
        const gallery = [p.image_url, ...(p.images || [])].filter(Boolean);
        const cur = gallery[detailImgIdx] || gallery[0];
        const out = !p.en_precommande && p.stock_restant <= 0;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDetailProduct(null)}>
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="b2b-product-detail">
              <button onClick={() => setDetailProduct(null)} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow" data-testid="b2b-detail-close">
                <X className="w-4 h-4" />
              </button>
              <div className="grid grid-cols-1 md:grid-cols-2 overflow-y-auto">
                {/* Galerie */}
                <div className="bg-slate-100 relative">
                  <div className="aspect-square w-full flex items-center justify-center">
                    {cur ? (
                      <img src={cur} alt="" className="w-full h-full object-cover" />
                    ) : (<ImageIcon className="w-16 h-16 text-slate-300" />)}
                  </div>
                  {gallery.length > 1 && (
                    <>
                      <button
                        onClick={() => setDetailImgIdx((i) => (i - 1 + gallery.length) % gallery.length)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
                        data-testid="b2b-detail-prev"
                      ><ChevronLeft className="w-4 h-4" /></button>
                      <button
                        onClick={() => setDetailImgIdx((i) => (i + 1) % gallery.length)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
                        data-testid="b2b-detail-next"
                      ><ChevronRight className="w-4 h-4" /></button>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                        {gallery.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setDetailImgIdx(i)}
                            className={`w-2 h-2 rounded-full ${i === detailImgIdx ? "bg-white" : "bg-white/50"}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
                {/* Infos */}
                <div className="p-5 space-y-3">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">{p.modele}</h2>
                    <p className="text-slate-500">{p.couleur}</p>
                  </div>
                  {p.en_precommande && (
                    <div className="text-xs bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-orange-800">
                      🕒 Précommande{preorderDateLabel(p.date_arrivee_estimee) ? ` — arrivage prévu : ${preorderDateLabel(p.date_arrivee_estimee)}` : " — date d'arrivée à confirmer"}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    {!discret && (
                      <p className="text-3xl font-bold text-indigo-600">{(p.prix_b2b_final_ht || 0).toFixed(2)} € <span className="text-sm font-normal text-slate-400">HT</span></p>
                    )}
                    {(() => { const b = stockBadge(p); return (
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${b.cls}`}>{b.label}</span>
                    ); })()}
                  </div>
                  {p.description && <p className="text-sm text-slate-600 whitespace-pre-line">{p.description}</p>}
                  <Button
                    onClick={() => { addToCart(p); setDetailProduct(null); }}
                    disabled={out}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 h-11"
                    data-testid="b2b-detail-add"
                  >
                    <Plus className="w-4 h-4 mr-2" />Ajouter au panier
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal — Saisie prix retail pour reçu client final */}
      {recuModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setRecuModal(null)}>
          <Card className="max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="b2b-recu-modal">
            <CardContent className="pt-5 space-y-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Reçu pour votre client</h2>
                <p className="text-xs text-slate-500 mt-0.5">Commande #{recuModal.commande.numero_commande} · {recuModal.commande.client_nom}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-xs text-amber-900">
                💡 Saisis les prix TTC que <strong>tu</strong> fais payer à ton client (pas les prix TopCocoon). Ce sont ceux qui apparaîtront sur son reçu.
              </div>
              <div className="space-y-2">
                {recuModal.lignes.map((l, i) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-50 rounded p-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{l.modele}</p>
                      <p className="text-xs text-slate-500">{l.couleur} · Qté {l.quantite}</p>
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Prix U. TTC"
                      value={l.prix_unitaire || ""}
                      onChange={(e) => updateLignePrix(i, e.target.value)}
                      className="w-28 text-right"
                      data-testid={`b2b-recu-prix-${i}`}
                    />
                    <span className="text-xs text-slate-500 w-6">€</span>
                  </div>
                ))}
              </div>
              <div className="border-t pt-3 flex justify-between items-baseline">
                <span className="text-slate-600">Total TTC affiché au client</span>
                <span className="text-2xl font-bold text-indigo-700" data-testid="b2b-recu-total">{totalRecuModal.toFixed(2)} €</span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setRecuModal(null)} className="flex-1" data-testid="b2b-recu-cancel">Annuler</Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const lignes = recuModal.lignes.filter(l => l.sku && l.prix_unitaire > 0).map(l => ({ sku: l.sku, prix_ttc_retail: l.prix_unitaire }));
                      await axios.put(`${API}/b2b/me/prix-retail-defaut`, { lignes }, { headers });
                      toast.success("Prix par défaut enregistrés — pré-remplis pour vos prochains reçus");
                    } catch { toast.error("Erreur"); }
                  }}
                  className="flex-1 text-indigo-600"
                  data-testid="b2b-recu-save-default"
                >
                  💾 Défaut
                </Button>
                <Button onClick={saveAndOpenRecu} className="flex-1 bg-indigo-600 hover:bg-indigo-700" disabled={totalRecuModal <= 0} data-testid="b2b-recu-generate">
                  <FileText className="w-4 h-4 mr-1" /> Générer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* BottomNav mobile — masqué pendant vérification/confirmation pour libérer l'espace */}
      {checkoutStep === "cart" && (
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.04)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="b2b-bottom-nav"
      >
        <div className="grid grid-cols-4">
          {BOTTOM_NAV.map((t) => {
            const Icon = t.icon;
            const isPlus = t.key === "__plus";
            const active = isPlus
              ? ["profil", "contact"].includes(tab) || showMoreMenu
              : tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => {
                  if (isPlus) { setShowMoreMenu(true); return; }
                  setTab(t.key);
                  if (t.key === "clients") loadMesClients();
                }}
                className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium ${active ? "text-indigo-600" : "text-slate-500"}`}
                data-testid={`b2b-nav-${t.key.replace("__", "")}`}
              >
                <Icon className={`w-5 h-5 ${active ? "text-indigo-600" : "text-slate-500"}`} />
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>
      )}

      {/* Menu Plus (mobile) */}
      {showMoreMenu && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setShowMoreMenu(false)}>
          <div
            className="w-full bg-white rounded-t-2xl overflow-hidden animate-in slide-in-from-bottom duration-200"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 flex justify-between items-center border-b">
              <p className="font-semibold text-slate-800">Plus</p>
              <button onClick={() => setShowMoreMenu(false)} className="text-slate-400 p-1" data-testid="b2b-more-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-2 flex flex-col">
              <button onClick={() => { setTab("profil"); setShowMoreMenu(false); }} className="flex items-center gap-3 px-3 py-3 text-left rounded-lg hover:bg-slate-50 active:bg-slate-100" data-testid="b2b-more-profil">
                <User className="w-5 h-5 text-slate-600" /> <span className="text-sm font-medium">Profil</span>
              </button>
              <button onClick={() => { setTab("contact"); setShowMoreMenu(false); }} className="flex items-center gap-3 px-3 py-3 text-left rounded-lg hover:bg-slate-50 active:bg-slate-100" data-testid="b2b-more-contact">
                <Mail className="w-5 h-5 text-slate-600" /> <span className="text-sm font-medium">Contact</span>
              </button>
              <div className="border-t my-1"></div>
              <button onClick={() => { setShowMoreMenu(false); logout(); }} className="flex items-center gap-3 px-3 py-3 text-left rounded-lg hover:bg-red-50 text-red-600" data-testid="b2b-more-logout">
                <LogOut className="w-5 h-5" /> <span className="text-sm font-medium">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2BEspace;
